import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Connexion réussie!');
      } else {
        await register(email, password, name);
        toast.success('Compte créé avec succès!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-sand flex">
      <div className="hidden md:block md:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1760681556426-24a4c42b8433?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxtYXJyYWtlY2glMjByZWQlMjBjaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MzIzMzY2OXww&ixlib=rb-4.1.0&q=85"
          alt="Moroccan Door"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
      </div>
      
      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-stone-200">
            <h1 className="font-playfair text-4xl font-bold text-center text-primary mb-2">
              {isLogin ? 'Connexion' : 'Inscription'}
            </h1>
            <p className="text-center text-charcoal/70 mb-6 font-nunito">
              {isLogin ? 'Bon retour parmi nous!' : 'Rejoignez notre communauté'}
            </p>

            {/* Google Sign-In Button - Official Google Branding */}
            <button
              onClick={handleGoogleLogin}
              data-testid="google-signin-btn"
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-stone-300 hover:border-stone-400 hover:bg-stone-50 text-charcoal font-nunito font-semibold py-3 px-6 rounded-xl transition-all shadow-sm hover:shadow-md mb-6"
            >
              {/* Official Google Logo */}
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continuer avec Google
            </button>

            {/* Separator */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-stone-200" />
              <span className="font-nunito text-sm text-charcoal/50">ou</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-nunito font-semibold text-charcoal mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    data-testid="name-input"
                    className="w-full bg-white border-2 border-stone-300 text-charcoal rounded-lg focus:border-primary focus:ring-0 px-4 py-3 font-nunito"
                    placeholder="Votre nom"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-nunito font-semibold text-charcoal mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="w-full bg-white border-2 border-stone-300 text-charcoal rounded-lg focus:border-primary focus:ring-0 px-4 py-3 font-nunito"
                  placeholder="votre@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-nunito font-semibold text-charcoal mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="w-full bg-white border-2 border-stone-300 text-charcoal rounded-lg focus:border-primary focus:ring-0 px-4 py-3 font-nunito"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] active:shadow-none hover:shadow-tile-hover disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  'Chargement...'
                ) : (
                  <>
                    {isLogin ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    {isLogin ? 'Se connecter' : "S'inscrire"}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
                className="text-secondary font-nunito font-semibold hover:underline"
              >
                {isLogin ? 'Pas encore de compte? Inscrivez-vous' : 'Déjà un compte? Connectez-vous'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;